import { Navigate, RouteObject, useLocation, useParams } from 'react-router-dom';
import React, { Suspense } from 'react';
import { lazyWithRetry } from '@tools/utils/lazyWithRetry.ts';
import { RequireStaff } from '@features/cms/components/RequireStaff.tsx';
import { CheckoutPage } from '@eshop/pages/checkout/CheckoutPage.tsx';
import { LoginPage, LogoutPage } from '@login/pages/LoginModal.tsx';
import { NoAccessPage } from '../auth/pages/NoAccessPage.tsx';
import suppliersRoutes from '@eshop/routes/suppliers/index.tsx';
import purchasingRoutes from '@erp/purchasing/routes/index.tsx';
import inventoryRoutes from '@erp/inventory/routes/index.tsx';
import cashflowRoutes from '@erp/cashflow/routes/index.tsx';
import cashSessionRoutes from '@erp/cash-session/routes/index.tsx';
import expensesRoutes from '@erp/expenses/routes/index.tsx';
import financeRoutes from '@erp/finance/routes/index.tsx';
import paymentsRoutes from '@erp/payments/routes/index.tsx';
import ordersRoutes from '@erp/orders/routes/index.tsx';
import quotesRoutes from '@erp/quotes/routes/index.tsx';
import commissionsRoutes from '@erp/commissions/routes/index.tsx';
import rrhhRoutes from '@erp/rrhh/routes/index.tsx';
import staffRoutes from '@erp/staff/routes/index.tsx';
import servicesRoutes from '@erp/services/routes/index.tsx';
import { getItemCreatePath } from '@eshop/pages/dashboard/items/itemCreationRoutes.ts';
import configurationRoutes from '@features/erp/configuration/routes/index.tsx';
// Importación agregada: rutas del módulo de suscripciones
import subscriptionsRoutes from '@features/erp/subscriptions/routes/index.tsx';
import { ERPOverviewPage } from '@erp/overview';
import PosScreen from '@erp/pos/PosScreen.tsx';
import { ClinicalHistoryPage } from '@erp/clinical-history/pages/ClinicalHistoryPage.tsx';
import { RequireApp } from '@components/guards/RequireApp.tsx';
import { Spin } from 'antd';
import { useAppSelector } from '@store/store.tsx';
import { selectSiteApps, selectSiteInfoStatus } from '@store/slices/settingsSlice.tsx';
import '@styles/LoadingScreen.css';
import { resolveDashboardHomePath } from './dashboardHome.ts';
import PublicNotFoundRedirect from '@components/public/PublicNotFoundRedirect.tsx';

import customersCrmRoutes from '@crm/customers/routes/index.tsx';
import remindersCrmRoutes from '@crm/reminders/routes/index.tsx';
import crmChatRoutes from '@crm/chat/routes/index.tsx';

// -----------------------------------------------------------------------------
// 1)  📌  Declaración central de páginas
// -----------------------------------------------------------------------------
// Los componentes se cargan de forma perezosa para reducir el bundle inicial
// react.dev recomienda dividir por rutas cuando sea posible
// const LoginPage = lazy(() => import('@pages/LoginPage.tsx'));
// const RegisterPage = lazy(() => import('@pages/RegisterPage.tsx'));
// const ResetPasswordPage = lazy(() => import('@pages/___ResetPasswordPage'));

const AccountPage = lazyWithRetry(() => import('@pages/AccountPage.tsx').then(m => ({ default: m.AccountPage })));
const AccountSchedulePage = lazyWithRetry(() => import('@pages/AccountPage.tsx').then(m => ({ default: m.AccountSchedulePage })));
const ForgotPasswordPage = lazyWithRetry(() =>
  import('../auth/pages/ForgotPasswordPage.tsx').then((m) => ({ default: m.ForgotPasswordPage })),
);
const SiteRedirect = lazyWithRetry(() => import('@components/SiteRedirect.tsx'));
const SiteBuilderPage = lazyWithRetry(() => import('../features/cms/pages/SiteBuilderPage.tsx'));
const CmsPageView = lazyWithRetry(() => import('../features/cms/pages/CmsPageView.tsx'));
const CatalogPage = lazyWithRetry(() => import('@eshop/pages/CatalogPage.tsx'));
const ItemDetailPage = lazyWithRetry(() => import('@eshop/pages/catalogue/DetailItem.tsx'));
const DashboardPage = lazyWithRetry(() => import('@eshop/pages/dashboard/DashboardPage.tsx'));
// const DashboardOverview = lazyWithRetry(() => import('@dashboard/overview/OverviewCard.tsx'));
const CartPage = lazyWithRetry(() => import('@eshop/pages/CartPage.tsx'));
const DashBoardCatalogue = lazyWithRetry(() => import('@eshop/pages/dashboard/items/DashBoardCatalogue.tsx'));
const DashBoardEditItemById = lazyWithRetry(() => import('@eshop/pages/dashboard/items/DashboardEditItem.tsx'));
const ItemCreationWizardPage = lazyWithRetry(() => import('@eshop/pages/dashboard/items/ItemCreationWizardPage.tsx'));
const SiteSettingsPage = lazyWithRetry(() => import('@pages/dashboard/SiteSettingsPage.tsx'));
const ShippingMethodsPage = lazyWithRetry(() => import('@eshop/pages/dashboard/ShippingMethodsPage.tsx'));
const OffersPage = lazyWithRetry(() => import('@eshop/pages/dashboard/offers/OffersPage.tsx'));
const OfferCreatePage = lazyWithRetry(() => import('@eshop/pages/dashboard/offers/OfferCreatePage.tsx'));
const OfferEditPage = lazyWithRetry(() => import('@eshop/pages/dashboard/offers/OfferEditPage.tsx'));
const CouponCreatePage = lazyWithRetry(() => import('@eshop/pages/dashboard/offers/CouponCreatePage.tsx'));
const BannersPage = lazyWithRetry(() => import('@eshop/pages/dashboard/banners/BannersPage.tsx'));
const PaymentsPage = lazyWithRetry(() => import('@eshop/pages/dashboard/PaymentsPage.tsx'));
const StatsPage = lazyWithRetry(() => import('@eshop/pages/dashboard/StatsPage.tsx'));
const EshopOrdersPage = lazyWithRetry(() => import('@eshop/pages/dashboard/orders/EshopOrdersPage.tsx'));
const ControlSitesPage = lazyWithRetry(() => import('@features/control/pages/ControlSitesPage.tsx'));
const FormsManagementPage = lazyWithRetry(() => import('@features/forms/FormsManagementPage.tsx'));
const AppointmentsPanel = lazyWithRetry(() => import('@erp/appointments/pages/AppointmentsPanel.tsx'));
const PreviewOrderPage = lazyWithRetry(() => import('@pages/order/PreviewOrderPage.tsx').then(m => ({ default: m.PreviewOrderPage })));
const PaymentReturnPage = lazyWithRetry(() => import('@pages/pay/PaymentReturnPage.tsx').then(m => ({ default: m.PaymentReturnPage })));
const GroupClassesAdminPage = lazyWithRetry(() =>
  import('@erp/appointments/pages/GroupClassesAdminPage.tsx').then((m) => ({ default: m.GroupClassesAdminPage })),
);
const ScheduleActivityPage = lazyWithRetry(() =>
  import('@erp/appointments/pages/ScheduleActivityPage.tsx').then((m) => ({ default: m.ScheduleActivityPage })),
);
// const SalesPanel = lazy(() => import('@erp/pages/SalesPanel.tsx'));
// importa aquí todos los demás componentes
// -----------------------------------------------------------------------------

const ADMIN_GROUPS = ['admin', 'erp:staff:admin'] as const;

type StaffGuardOptions = Omit<React.ComponentProps<typeof RequireStaff>, 'children' | 'render'>;

const withStaffGuard = (Component: React.ElementType, options?: StaffGuardOptions): React.FC => {
  const GuardedComponent: React.FC = () => (
    <RequireStaff {...options} render={() => React.createElement(Component)} />
  );
  GuardedComponent.displayName = `RequireStaffGuard`;
  return GuardedComponent;
};

const StaffDashboardPage = withStaffGuard(DashboardPage);
const StaffDashBoardCatalogue = withStaffGuard(DashBoardCatalogue);
const StaffDashBoardEditItemById = withStaffGuard(DashBoardEditItemById);
const StaffItemCreationWizardPage = withStaffGuard(ItemCreationWizardPage);
const LegacyItemCreateRedirect = () => <Navigate to={getItemCreatePath('eshop')} replace />;
// The dashboard home resolver can select /erp from the enabled-app list. If
// this root route is not accessible for the current staff member, sending it
// back to /dashboard would select /erp again and create a redirect loop.
const StaffERPOverviewPage = withStaffGuard(ERPOverviewPage, { fallbackPath: '/no-access' });
const StaffSiteSettingsPage = withStaffGuard(SiteSettingsPage);
const AdminSiteSettingsPage = withStaffGuard(SiteSettingsPage, { requiredGroups: ADMIN_GROUPS });
const StaffShippingMethodsPage = withStaffGuard(ShippingMethodsPage);
const StaffPaymentsPage = withStaffGuard(PaymentsPage);
const StaffOffersPage = withStaffGuard(OffersPage);
const AdminOfferCreatePage = withStaffGuard(OfferCreatePage, { requiredGroups: ADMIN_GROUPS });
const AdminOfferEditPage = withStaffGuard(OfferEditPage, { requiredGroups: ADMIN_GROUPS });
const AdminCouponCreatePage = withStaffGuard(CouponCreatePage, { requiredGroups: ADMIN_GROUPS });
const ErpOffersPage = withStaffGuard(OffersPage, {
  requiredApps: ['erp', 'erp__sales__offers'],
  fallbackPath: '/no-access',
});
const ErpOfferCreatePage = withStaffGuard(OfferCreatePage, {
  requiredApps: ['erp', 'erp__sales__offers'],
  requiredGroups: ADMIN_GROUPS,
  fallbackPath: '/no-access',
});
const ErpOfferEditPage = withStaffGuard(OfferEditPage, {
  requiredApps: ['erp', 'erp__sales__offers'],
  requiredGroups: ADMIN_GROUPS,
  fallbackPath: '/no-access',
});
const ErpCouponCreatePage = withStaffGuard(CouponCreatePage, {
  requiredApps: ['erp', 'erp__sales__offers'],
  requiredGroups: ADMIN_GROUPS,
  fallbackPath: '/no-access',
});
const StaffBannersPage = withStaffGuard(BannersPage);
const StaffStatsPage = withStaffGuard(StatsPage);
const StaffEshopOrdersPage = withStaffGuard(EshopOrdersPage);
const StaffControlSitesPage = withStaffGuard(ControlSitesPage, { requiredApps: ['av_control'] });
const StaffFormsManagementPage = withStaffGuard(FormsManagementPage);

const SiteBuilderRoute = () => (
  <RequireStaff
    render={() => (
      <Suspense fallback={<div>Loading...</div>}>
        <SiteBuilderPage />
      </Suspense>
    )}
  />
);

const LegacyWebInlineRedirect = () => {
  const { slug } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('editMode', 'true');
  const base = slug ? `/web/${encodeURIComponent(slug)}` : '/web';
  const query = params.toString();
  return <Navigate to={`${base}${query ? `?${query}` : ''}${location.hash}`} replace />;
};

const LegacyWebEditorRoute = () => (
  <RequireStaff render={() => <LegacyWebInlineRedirect />} />
);

const LegacyWebEditorAliasRoute = () => (
  <RequireStaff render={() => <LegacyWebInlineRedirect />} />
);

const DashboardErpRedirect: React.FC = () => {
  const { pathname, search, hash } = useLocation();
  const normalizedPath = pathname.replace(/^\/dashboard\/erp/, '/erp');
  const targetPath = normalizedPath.startsWith('/erp') ? normalizedPath : '/erp';

  return <Navigate to={`${targetPath}${search}${hash}`} replace />;
};

const DashboardHomeRedirect: React.FC = () => {
  const apps = useAppSelector(selectSiteApps) ?? [];
  const infoStatus = useAppSelector(selectSiteInfoStatus);
  const location = useLocation();

  if (infoStatus === 'loading') {
    return (
      <div className="loading-screen">
        <Spin size="large" tip="Cargando..." />
      </div>
    );
  }

  const targetPath = resolveDashboardHomePath(apps);

  return <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />;
};

const StaffDashboardHomeRedirect = withStaffGuard(DashboardHomeRedirect);

const EshopIndexRedirect: React.FC = () => (
  <Navigate to="/eshop/catalogue" replace />
);


// --- App-guarded wrapper for eshop pages ---
const withEshopGuard = <P extends object>(Component: React.ComponentType<P>): React.FC<P> => {
  const GuardedComponent: React.FC<P> = (props) => (
    <RequireApp requiredApp="eshop">
      {React.createElement(Component, props)}
    </RequireApp>
  );
  GuardedComponent.displayName = `RequireEshopGuard`;
  return GuardedComponent;
};

// --- App-guarded wrapper for cms pages ---
const withCmsAppGuard = <P extends object>(Component: React.ComponentType<P>): React.FC<P> => {
  const GuardedComponent: React.FC<P> = (props) => (
    <RequireApp requiredApp="cms">
      {React.createElement(Component, props)}
    </RequireApp>
  );
  GuardedComponent.displayName = `RequireCmsAppGuard`;
  return GuardedComponent;
};

// --- App-guarded wrapper for erp pages ---
export const withErpGuard = <P extends object>(Component: React.ComponentType<P>): React.FC<P> => {
  const GuardedComponent: React.FC<P> = (props) => (
    <RequireApp requiredApp="erp">
      {React.createElement(Component, props)}
    </RequireApp>
  );
  GuardedComponent.displayName = `RequireErpGuard`;
  return GuardedComponent;
};

// Guarded page components
const GuardedEshopIndexRedirect = withEshopGuard(EshopIndexRedirect);
const GuardedCartPage = withEshopGuard(CartPage);
const GuardedCatalogPage = withEshopGuard(CatalogPage);
const GuardedItemDetailPage = withEshopGuard(ItemDetailPage);
const GuardedCheckoutPage = withEshopGuard(CheckoutPage);
const GuardedCmsPageView = withCmsAppGuard(CmsPageView);

export interface ComponentPage {
  component: React.ElementType; // acepta function components, class components y lazy
  handle?: RouteObject['handle'];
}

export interface ComponentOrMapping {
  [key: string]: React.ElementType | ComponentPage | ComponentOrMapping;
}

export const isMappingNode = (value: unknown): value is ComponentOrMapping => {
  if (!value || typeof value !== 'object') return false;
  if (Array.isArray(value)) return false;
  if ('component' in (value as Record<string, unknown>)) return false;
  if ('$$typeof' in (value as Record<string, unknown>)) return false;
  return true;
};

const extractMappingBranch = (
  mapping: ComponentOrMapping,
  branchKey: string,
): ComponentOrMapping => {
  const branch = (mapping as Record<string, unknown>)[branchKey];
  if (!isMappingNode(branch)) {
    return {};
  }
  return branch as ComponentOrMapping;
};

const omitMappingBranches = (
  mapping: ComponentOrMapping,
  branchKeys: readonly string[],
): ComponentOrMapping => {
  const result: ComponentOrMapping = {};

  Object.entries(mapping).forEach(([key, value]) => {
    if (branchKeys.includes(key)) {
      return;
    }
    result[key] = value;
  });

  return result;
};

const mergeComponentMappings = (
  base: ComponentOrMapping,
  extra: ComponentOrMapping,
): ComponentOrMapping => {
  const result: ComponentOrMapping = { ...base };

  Object.entries(extra).forEach(([key, value]) => {
    const existing = result[key];
    if (isMappingNode(existing) && isMappingNode(value)) {
      result[key] = mergeComponentMappings(
        existing as ComponentOrMapping,
        value as ComponentOrMapping,
      );
    } else {
      result[key] = value;
    }
  });

  return result;
};

const mergeMappingList = (mappings: ComponentOrMapping[]): ComponentOrMapping => {
  return mappings.reduce<ComponentOrMapping>(
    (acc, mapping) => mergeComponentMappings(acc, mapping),
    {},
  );
};

const erpRouteModules = [
  suppliersRoutes,
  purchasingRoutes,
  inventoryRoutes,
  cashflowRoutes,
  cashSessionRoutes,
  expensesRoutes,
  financeRoutes,
  paymentsRoutes,
  ordersRoutes,
  quotesRoutes,
  commissionsRoutes,
  rrhhRoutes,
  staffRoutes,
  servicesRoutes,
  configurationRoutes,
  // Módulo de suscripciones agregado
  subscriptionsRoutes,

  // CRM
  customersCrmRoutes,
  remindersCrmRoutes,
  crmChatRoutes,
];

const erpFeatureRoutes = mergeMappingList(
  erpRouteModules.map((mapping) => extractMappingBranch(mapping, 'erp')),
);

const erpSupplementalRoutes = mergeMappingList(
  erpRouteModules.map((mapping) => omitMappingBranches(mapping, ['erp'])),
);

const erpBaseRoutes: ComponentOrMapping = {
  '': { component: StaffERPOverviewPage },
  account: { component: AccountPage },
  sale: { component: PosScreen },
  appointments: {
    '': { component: AppointmentsPanel },
    classes: { component: GroupClassesAdminPage },
    activity: { component: ScheduleActivityPage },
  },
  'clinical-history': { component: withStaffGuard(ClinicalHistoryPage) },
  configuration: {
    parameters: { component: StaffSiteSettingsPage },
  },
  offers: {
    overview: { component: ErpOffersPage, handle: { breadcrumb: 'Ofertas y cupones' } },
    create: { component: ErpOfferCreatePage, handle: { breadcrumb: 'Crear oferta' } },
    edit: { ':offerId': { component: ErpOfferEditPage, handle: { breadcrumb: 'Editar oferta' } } },
    coupon: { create: { component: ErpCouponCreatePage, handle: { breadcrumb: 'Crear cupón' } } },
  },
};

const erpRoutes = mergeComponentMappings(erpBaseRoutes, erpFeatureRoutes);

// Mis páginas declaradas con paths anidados
const baseComponentMapping: ComponentOrMapping = {
  // Ej: auth/login-page  →   data-path="auth/login-page"
  auth: {
    login: { component: LoginPage },
    logout: { component: LogoutPage },
    'forgot-password': { component: ForgotPasswordPage },
    // register: { component: RegisterPage },
    // 'reset-pass': { component: ResetPasswordPage },
  },
  'no-access': { component: NoAccessPage },
  user: {
    account: { component: AccountPage },
    schedule: { component: AccountSchedulePage },
  },
  control: {
    sites: { component: StaffControlSitesPage },
  },
  crm: {
    forms: { overview: { component: StaffFormsManagementPage, handle: { breadcrumb: 'Formularios' } } },
  },
  cms: {
    account: { component: AccountPage },
    grapesjs: {
      ':slug?': { component: SiteBuilderRoute },
    },
  },
  eshop: {
    '': { component: GuardedEshopIndexRedirect },
    account: { component: AccountPage },
    cart: { component: GuardedCartPage },
    catalogue: { component: GuardedCatalogPage },
    'category/*': { component: GuardedCatalogPage },
    'detail/:slug_id': { component: GuardedItemDetailPage },
    checkout: {
      '': { component: GuardedCheckoutPage },
      preview: {
        ':previewHash': { component: PreviewOrderPage },
      },
    },
    orders: {
      ':orderId': { component: PreviewOrderPage },
      public: {
        ':publicId': { component: PreviewOrderPage },
      },
    },
    dashboard: {
      '': { component: StaffDashboardPage },
      erp: { component: DashboardErpRedirect },
      orders: { component: StaffEshopOrdersPage },
      items: {
        overview: { component: StaffDashBoardCatalogue },
        create: { component: StaffItemCreationWizardPage },
        edit: { component: LegacyItemCreateRedirect },
        'edit/:id': StaffDashBoardEditItemById,
      },
      shipping: {
        overview: { component: StaffShippingMethodsPage },
      },
      payments: {
        overview: { component: StaffPaymentsPage },
      },
      offers: {
        overview: { component: StaffOffersPage, handle: { breadcrumb: 'Ofertas y cupones' } },
        create: { component: AdminOfferCreatePage, handle: { breadcrumb: 'Crear oferta' } },
        edit: {
          ':offerId': { component: AdminOfferEditPage, handle: { breadcrumb: 'Editar oferta' } },
        },
        coupon: {
          create: { component: AdminCouponCreatePage, handle: { breadcrumb: 'Crear cupón' } },
        },
      },
      banners: {
        overview: { component: StaffBannersPage },
      },
      stats: { component: StaffStatsPage },
      settings: {
        component: AdminSiteSettingsPage,
        handle: { breadcrumb: 'settings' },
      },
    },
  },
  erp: erpRoutes,
  dashboard: {
    '': { component: StaffDashboardHomeRedirect },
    erp: { component: DashboardErpRedirect },
    settings: {
      component: StaffSiteSettingsPage,
      handle: { breadcrumb: 'settings' },
    },
    // orders: { component: StaffEshopOrdersPage },
  },
  track: {
    ':accessToken': { component: PreviewOrderPage },
  },
  pay: {
    return: {
      ':provider': { component: PaymentReturnPage },
    },
  },
  'site-builder': {
    ':slug?': { component: LegacyWebEditorRoute },
  },
  'legacy-site-builder': {
    ':slug?': { component: LegacyWebEditorAliasRoute },
  },
  web: {
    ':slug?': { component: GuardedCmsPageView },
  },
};

const aggregatedFeatureRoutes = erpSupplementalRoutes;

export const componentMapping: ComponentOrMapping = mergeComponentMappings(
  baseComponentMapping,
  aggregatedFeatureRoutes,
);


// -----------------------------------------------------------------------------
// 2)  📌  Helpers de type‑guard y conversión a RouteObject[]
// -----------------------------------------------------------------------------

/**
 * Devuelve true si v tiene una clave 'component'. No asume que sea function:
 * puede ser lazy/forwardRef/memo (objetos con $$typeof).
 */
const hasComponentProp = (v: unknown): v is ComponentPage => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return !!v && typeof v === 'object' && 'component' in (v as any);
};

/**
 * Devuelve true si v es un "element type" válido para React.createElement.
 * Cubre function components/clases y objetos con $$typeof (lazy, memo, forwardRef).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isElementType = (v: unknown): v is React.ElementType => {
  return (
    typeof v === 'function' ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (typeof v === 'object' && v !== null && '$$typeof' in (v as any))
  );
};

export const buildRouteObjects = (
  map: ComponentOrMapping,
  base = '',
): RouteObject[] => {
  const routes: RouteObject[] = [];

  Object.entries(map).forEach(([key, val]) => {
    // Construye la ruta y quita barras duplicadas o finales
    const currentPath = `${base}/${key}`
      .replace(/\/+/g, '/') // evita '//'
      .replace(/\/$/, ''); // evita que termine en '/'

    if (hasComponentProp(val)) {
      const Comp = val.component;
      if (!isElementType(Comp)) {
        console.warn(`'component' no es un React.ElementType válido para la ruta ${currentPath}`, val);
        return;
      }
      const routePath = currentPath.endsWith('*') ? currentPath : `${currentPath}/*`;
      routes.push({
        path: routePath, // <— admite /home y /home/
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            {React.createElement(Comp)}
          </Suspense>
        ),
        handle: val.handle,
      });
      return;
    }

    if (isElementType(val)) {
      const routePath = currentPath.endsWith('*') ? currentPath : `${currentPath}/*`;
      routes.push({
        path: routePath, // <— admite /home y /home/
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            {React.createElement(val)}
          </Suspense>
        ),
      });
      return;
    }

    if (val && typeof val === 'object') {
      // Sub‑árbol de rutas
      routes.push(...buildRouteObjects(val as ComponentOrMapping, currentPath));
      return;
    }

    console.warn(`Valor inesperado para la ruta en ${currentPath}:`, val);
  });

  return routes;
};

// Ruta por defecto y 404 de ejemplo -------------
const LoginRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`/auth/login${location.search}`} replace />;
};

export const appRoutes: RouteObject[] = [
  { path: '/', element: <SiteRedirect /> },
  { path: '/login', element: <LoginRedirect /> },
  ...buildRouteObjects(componentMapping),
  { path: '*', element: <PublicNotFoundRedirect /> },
];
